import { useMemo } from 'react';
import {
  getArtifactRowKey,
  useBulkArtifactDetail,
  type ProjectArtifact,
} from '@/entities/project-artifact';
import { resolveWidgetThumbnailComponent } from '@/shared/ui/WidgetThumbnail';
import { PageTransition } from '@/shared/ui/animations';
import { getStaggerDelay } from '@/shared/constants/motion.constants';
import { ArtifactCard } from '../ArtifactCard';
import { ArtifactCardSkeleton } from '../ArtifactCardSkeleton';

interface Props {
  projectId: string;
  artifacts: ProjectArtifact[];
  isLoading: boolean;
  onDeleteArtifact?: (artifact: ProjectArtifact) => void;
  /** Disables the per-card detach/delete action with a permission tooltip. */
  deleteDisabled?: boolean;
}

const SKELETON_COUNT = 6;

function stringOrNull(value: unknown): string | null {
  return typeof value === 'string' ? value : null;
}

export function ArtifactsGrid(props: Props) {
  const showSkeletons = props.isLoading && props.artifacts.length === 0;

  // Only the rows whose widget kind has a thumbnail renderer need render-data;
  // image/video/file/insight rows fall back to the palette icon (or their
  // poster <img>) and stay out of the bulk request.
  const widgetRowKeys = useMemo(
    () =>
      props.artifacts
        .filter((artifact) => resolveWidgetThumbnailComponent(stringOrNull(artifact.artifactKey)))
        .map(getArtifactRowKey),
    [props.artifacts],
  );

  const { data: details } = useBulkArtifactDetail(props.projectId, widgetRowKeys);

  // Map each row key to its render-data so every card reads its own payload.
  const dataByRowKey = useMemo(() => {
    const map = new Map<string, Record<string, unknown> | null>();
    for (const detail of details ?? []) {
      map.set(getArtifactRowKey(detail), detail.data ?? null);
    }
    return map;
  }, [details]);

  return (
    <div className="grid gap-4 [grid-template-columns:repeat(auto-fill,minmax(min(320px,100%),1fr))]">
      {showSkeletons
        ? Array.from({ length: SKELETON_COUNT }).map((_, i) => <ArtifactCardSkeleton key={i} />)
        : props.artifacts.map((artifact, index) => (
            <PageTransition key={artifact.id} delay={getStaggerDelay(index)}>
              <ArtifactCard
                artifact={artifact}
                widgetData={dataByRowKey.get(getArtifactRowKey(artifact))}
                onDelete={props.onDeleteArtifact}
                deleteDisabled={props.deleteDisabled}
              />
            </PageTransition>
          ))}
    </div>
  );
}
